/**
 * EventDrop Pro ✦ — dedicated login.
 *
 * NOT a second auth system — uses /api/auth/login-email exactly like
 * the host login. The only difference is presentation, copy, and
 * post-login routing (always returns to /pro, never to /dashboard).
 *
 * Visual identity (per Craig's 2026-05-15 brief):
 *   • Quieter than host login — studio-leaning, not consumer warm
 *   • Darker walnut tones, restrained gold, larger spacing
 *   • "Enter the studio ✦" framing, NOT "Operator authentication portal"
 *   • Emotional copy throughout. Never "Unauthorized" / "403" /
 *     "Access denied" — failed login reads as "the room hasn't
 *     recognised you tonight ✦".
 *
 * Reuses the existing AuthContext.login(user, token). The session
 * token is identical to a host's. The /pro Pro-flag check is
 * server-side via require_pro_operator on every /api/pro/* call —
 * this login screen never trusts a frontend flag.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Studio-leaning palette — derived from MODE_TWO but pushed darker
// and quieter. The Pro pages share this so the operator experience
// reads cohesive across login + dashboard.
export const PRO_PALETTE = {
  bg: '#0A0805',         // darker walnut than host (#100A04)
  bgRaised: '#15100A',
  panel: 'rgba(20, 14, 8, 0.94)',
  ink: '#F4E9D2',
  inkMid: 'rgba(244,233,210,0.78)',
  inkMuted: 'rgba(244,233,210,0.54)',
  inkFaint: 'rgba(244,233,210,0.34)',
  gold: '#C29D52',        // restrained — pulled back from #D4AA52
  goldSoft: 'rgba(194,157,82,0.72)',
  goldHair: 'rgba(194,157,82,0.22)',
  serif: '"EB Garamond", "Cormorant Garamond", Georgia, serif',
};

export default function ProLoginPage() {
  const { user, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Read ?return=/pro/whatever for callers that want to land
  // somewhere specific after login. Default to /pro.
  const returnTo = new URLSearchParams(location.search).get('return') || '/pro';

  // If a user is already signed in and is a Pro operator, skip the
  // form entirely. The token + AuthContext are already correct.
  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    // Server is the source of truth — but if the cached user
    // already has the flag we can short-circuit. Non-operators
    // are bounced to the calm "studio is closed" message rendered
    // by ProSessionsPage's not-found state.
    if (user.is_pro_operator) {
      navigate(returnTo, { replace: true });
    } else {
      navigate('/pro', { replace: true });
    }
  }, [user, authLoading, navigate, returnTo]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError('A name and a key are needed to enter the studio.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/auth/login-email`, {
        email: email.trim().toLowerCase(),
        password,
      });
      const { user: u, session_token } = r.data || {};
      if (!u || !session_token) {
        setError('The room could not be opened. Try again in a moment.');
        return;
      }
      login(u, session_token);
      // Server-side require_pro_operator gates every /api/pro/* call,
      // so a non-Pro who somehow lands here will see the gentle
      // "This room is not open tonight ✦" message on /pro itself.
      navigate(returnTo, { replace: true });
    } catch (err) {
      // Emotional language only — NEVER "Invalid credentials".
      const status = err?.response?.status;
      if (status === 401) {
        setError("The room hasn't recognised you tonight. Check the name and key, then knock again.");
      } else {
        setError('The studio is quiet just now. Try once more in a moment.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="pro-login-page"
      style={{
        minHeight: '100vh',
        background: PRO_PALETTE.bg,
        // Quiet vignette — gold hair at top + bottom, faint walnut
        // grain across the body. No imagery, no hero photo, no
        // splash. Reads as a closed studio door.
        backgroundImage: `
          radial-gradient(ellipse 80% 50% at 50% -10%, rgba(194,157,82,0.06), transparent 60%),
          radial-gradient(ellipse 60% 40% at 50% 110%, rgba(194,157,82,0.04), transparent 60%)
        `,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(24px, 5vw, 80px)',
      }}
    >
      {/* Wordmark — top left on desktop, centered on mobile. */}
      <div style={{
        position: 'absolute', top: 28, left: 'clamp(20px, 4vw, 48px)',
        fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
        fontSize: 13, letterSpacing: '0.32em', textTransform: 'uppercase',
        color: PRO_PALETTE.goldSoft,
      }}>
        EventDrop Pro ✦
      </div>

      {/* The studio door */}
      <form
        onSubmit={submit}
        data-testid="pro-login-form"
        style={{
          width: 'min(440px, 100%)',
          padding: 'clamp(36px, 5vw, 56px) clamp(28px, 5vw, 48px)',
          background: PRO_PALETTE.panel,
          border: `1px solid ${PRO_PALETTE.goldHair}`,
          borderRadius: 4,
          boxShadow: '0 36px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(194,157,82,0.05)',
          backdropFilter: 'blur(12px)',
        }}
      >
        <div style={{
          fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
          fontSize: 10.5, letterSpacing: '0.32em', textTransform: 'uppercase',
          color: PRO_PALETTE.goldSoft, marginBottom: 14, textAlign: 'center',
        }}>
          ✦ The Studio ✦
        </div>
        <h1
          data-testid="pro-login-heading"
          style={{
            margin: 0, marginBottom: 10,
            fontFamily: PRO_PALETTE.serif, fontWeight: 500,
            fontSize: 'clamp(30px, 4.5vw, 40px)',
            color: PRO_PALETTE.ink,
            lineHeight: 1.1, letterSpacing: '-0.012em',
            textAlign: 'center',
          }}
        >
          Enter the studio ✦
        </h1>
        <p style={{
          margin: 0, marginBottom: 30,
          fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
          fontSize: 14, color: PRO_PALETTE.inkMuted, lineHeight: 1.55,
          textAlign: 'center',
        }}>
          Where the booths, the galleries, and the live nights are kept.
        </p>

        <label style={{ display: 'block', marginBottom: 18 }}>
          <div style={{
            fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
            fontSize: 10.5, letterSpacing: '0.24em', textTransform: 'uppercase',
            color: PRO_PALETTE.goldSoft, marginBottom: 8,
          }}>The name on the door</div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            data-testid="pro-login-email"
            style={{
              width: '100%', padding: '13px 16px',
              background: PRO_PALETTE.bgRaised,
              border: `1px solid ${PRO_PALETTE.goldHair}`,
              borderRadius: 3,
              fontFamily: PRO_PALETTE.serif, fontSize: 16, fontStyle: 'italic',
              color: PRO_PALETTE.ink, outline: 'none',
            }}
          />
        </label>

        <label style={{ display: 'block', marginBottom: 22 }}>
          <div style={{
            fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
            fontSize: 10.5, letterSpacing: '0.24em', textTransform: 'uppercase',
            color: PRO_PALETTE.goldSoft, marginBottom: 8,
          }}>The key</div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            data-testid="pro-login-password"
            style={{
              width: '100%', padding: '13px 16px',
              background: PRO_PALETTE.bgRaised,
              border: `1px solid ${PRO_PALETTE.goldHair}`,
              borderRadius: 3,
              fontFamily: PRO_PALETTE.serif, fontSize: 16, fontStyle: 'italic',
              color: PRO_PALETTE.ink, outline: 'none',
              letterSpacing: '0.05em',
            }}
          />
        </label>

        {error && (
          <div
            data-testid="pro-login-error"
            style={{
              marginBottom: 20, padding: '12px 14px',
              background: 'rgba(177,67,31,0.10)',
              border: '1px solid rgba(177,67,31,0.35)',
              borderRadius: 3,
              fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
              fontSize: 13, color: '#E7B79C', lineHeight: 1.5,
            }}
          >
            ✦ {error}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid="pro-login-submit"
          style={{
            width: '100%', padding: '13px 18px',
            background: busy ? 'rgba(194,157,82,0.18)' : PRO_PALETTE.gold,
            border: `1px solid ${PRO_PALETTE.gold}`,
            borderRadius: 3,
            fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
            fontSize: 14.5, letterSpacing: '0.04em',
            color: busy ? PRO_PALETTE.inkMuted : '#1A1208',
            cursor: busy ? 'wait' : 'pointer',
            transition: 'background 220ms ease, color 220ms ease',
          }}
        >
          {busy ? 'Opening the door…' : 'Enter the studio ✦'}
        </button>

        <div style={{
          marginTop: 22, textAlign: 'center',
          fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
          fontSize: 12.5, color: PRO_PALETTE.inkFaint, letterSpacing: '0.005em',
        }}>
          Not the studio you meant?  ·  <Link
            to="/login"
            style={{ color: PRO_PALETTE.goldSoft, textDecoration: 'underline' }}
            data-testid="pro-login-host-link"
          >
            Host sign in
          </Link>
        </div>
      </form>
    </div>
  );
}
